import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronLeft, ChevronRight, ArrowRight } from "lucide-react";
import { http, formatPrice, SUBCATEGORY_IMAGES } from "@/lib/api";

const PALETTES = [
  "from-[#7b341e] to-[#c05621]",
  "from-[#1a365d] to-[#2b6cb0]",
  "from-[#44337a] to-[#6b46c1]",
  "from-[#22543d] to-[#38a169]",
  "from-[#702459] to-[#b83280]",
  "from-[#744210] to-[#b7791f]",
];

const BannerCarousel = () => {
  const [slides, setSlides] = useState([]);
  const [idx, setIdx] = useState(0);

  useEffect(() => {
    http.get("/products").then((r) => {
      const groups = {};
      for (const p of r.data.products) {
        if (!p.subcategory) continue;
        const key = `${p.category}||${p.subcategory}`;
        if (!groups[key]) groups[key] = { category: p.category, sub: p.subcategory, items: [] };
        groups[key].items.push(p);
      }
      const built = Object.values(groups)
        .map((g) => ({
          category: g.category,
          sub: g.sub,
          count: g.items.length,
          from: Math.min(...g.items.map((i) => i.price)),
          image: g.items[0].image_url,
          header: SUBCATEGORY_IMAGES[g.sub] || null,
        }))
        .slice(0, 8);
      setSlides(built);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    if (slides.length < 2) return;
    const t = setInterval(() => setIdx((i) => (i + 1) % slides.length), 4000);
    return () => clearInterval(t);
  }, [slides.length]);

  if (slides.length === 0) {
    return <div className="h-[160px] sm:h-[280px] rounded-sm fk-shadow bg-gradient-to-r from-[#7b341e] to-[#c05621]" data-testid="banner-carousel" />;
  }

  const go = (n) => setIdx((n + slides.length) % slides.length);
  const s = slides[idx];

  return (
    <div
      className="relative overflow-hidden rounded-sm fk-shadow group"
      data-testid="banner-carousel"
    >
      <Link
        to={`/category/${encodeURIComponent(s.category)}?sub=${encodeURIComponent(s.sub)}`}
        className={`relative h-[160px] sm:h-[280px] bg-gradient-to-r ${PALETTES[idx % PALETTES.length]} flex items-center`}
        data-testid="banner-slide-link"
      >
        {s.header && (
          <>
            <img src={s.header} alt={s.sub} className="absolute inset-0 w-full h-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-r from-black/75 via-black/45 to-transparent" />
          </>
        )}
        <div className="relative z-10 flex-1 px-6 sm:px-14 text-white">
          <p className="text-xs sm:text-sm font-medium uppercase tracking-wider text-[#ffe500]">
            {s.category}
          </p>
          <h2 className="font-head text-2xl sm:text-5xl font-extrabold mt-1 max-w-xl drop-shadow">
            {s.sub}
          </h2>
          <p className="text-sm sm:text-lg mt-2 text-gray-100">
            {s.count} handcrafted piece{s.count > 1 ? "s" : ""} · from {formatPrice(s.from)}
          </p>
          <span className="inline-flex items-center gap-1.5 mt-3 bg-white/15 backdrop-blur-sm text-white text-xs sm:text-sm font-semibold px-4 py-1.5 rounded-full border border-white/30">
            Shop now <ArrowRight className="w-3.5 h-3.5" />
          </span>
        </div>
        {!s.header && (
          <div className="hidden sm:flex w-[340px] h-full items-center justify-center p-6">
            <img
              src={s.image}
              alt={s.sub}
              className="max-h-full max-w-full object-contain drop-shadow-2xl mix-blend-multiply"
            />
          </div>
        )}
      </Link>

      <button
        onClick={() => go(idx - 1)}
        className="absolute left-0 top-1/2 -translate-y-1/2 z-20 bg-white/90 text-gray-700 h-14 w-9 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
        data-testid="banner-prev"
        aria-label="Previous"
      >
        <ChevronLeft className="w-6 h-6" />
      </button>
      <button
        onClick={() => go(idx + 1)}
        className="absolute right-0 top-1/2 -translate-y-1/2 z-20 bg-white/90 text-gray-700 h-14 w-9 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
        data-testid="banner-next"
        aria-label="Next"
      >
        <ChevronRight className="w-6 h-6" />
      </button>

      <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-1.5 z-20">
        {slides.map((_, i) => (
          <span
            key={i}
            className={`h-1.5 rounded-full transition-all ${
              i === idx ? "w-5 bg-white" : "w-1.5 bg-white/60"
            }`}
          />
        ))}
      </div>
    </div>
  );
};

export default BannerCarousel;
